<template>
  <div class="loop-column-slider w-full">
    <TLabel>{{ $t("forms.loop_column-size") }}</TLabel>
    <vue-slider
      ref="slider"
      v-model="columnsWidths"
      :min="0"
      :max="100"
      width="100%"
      :height="6"
      :dot-size="16"
      :duration="0.3"
      :min-range="10"
      :process="columnProsses"
      @drag-start="showTooltip = true"
      @drag-end="showTooltip = false"
    >
      <template #process="{ start, end, style }">
        <div class="vue-slider-process" :style="style">
          <div
            v-if="showTooltip"
            :class="[
              'merge-tooltip',
              'vue-slider-dot-tooltip-inner',
              'vue-slider-dot-tooltip-inner-top',
            ]"
          >
            {{ Math.round(end - start) }}%
          </div>
        </div>
      </template>
    </vue-slider>
  </div>
</template>
<script lang="ts" setup>
  import { useMjmlStore } from "@/stores/mjmlStore";
  import { TLabel } from "@topol/twig-ui";
  import VueSlider, { ProcessOption } from "vue-slider-component";
  import { computed, ref } from "vue";

  const mjmlStore = useMjmlStore();

  const showTooltip = ref(false);
  const slider = ref();

  const columnsWidths = computed({
    get() {
      const activeBlock = mjmlStore.getActiveBlock;
      if (!activeBlock || activeBlock.tagName !== "mj-loop") return [];
      const columns = activeBlock.children;
      if (columns.length == 0) return [];
      const array: number[] = [parseInt(columns[0].attributes.width)];
      for (let i = 1; i < columns.length - 1; i++) {
        array.push(
          array[array.length - 1] + parseInt(columns[i].attributes.width)
        );
      }
      return array;
    },
    set(val: number[]) {
      if (typeof val === "number") {
        val = [val];
      }
      if (val[0] < 10) {
        slider.value.setValue(columnsWidths.value);
        return;
      }
      if (val[val.length - 1] > 90) {
        slider.value.setValue(columnsWidths.value);
        return;
      }
      updateWidths(val);
    },
  });

  function updateWidths(val: number[]) {
    mjmlStore.takeSnapshot();
    const activeBlock = mjmlStore.getActiveBlock;
    if (!activeBlock || activeBlock.tagName !== "mj-loop") return;
    const columns = activeBlock.children;
    if (columns.length == 0) return;
    columns[0].attributes.width = `${val[0]}%`;
    for (const block of columns[0].children) {
      block.attributes.containerWidth = Math.round(
        activeBlock.attributes.containerWidth * (val[0] / 100)
      );
    }
    for (let i = 1; i < columns.length; i++) {
      const width = val[i] - val[i - 1];
      columns[i].attributes.width = `${width}%`;
      for (const block of columns[i].children) {
        block.attributes.containerWidth = Math.round(
          activeBlock.attributes.containerWidth * (width / 100)
        );
      }
    }
    columns[columns.length - 1].attributes.width = `${
      100 - val[val.length - 1]
    }%`;
    for (const block of columns[columns.length - 1].children) {
      block.attributes.containerWidth = Math.round(
        activeBlock.attributes.containerWidth *
          (100 - val[val.length - 1] / 100)
      );
    }
  }

  function columnProsses(dotsPos: number[]): ProcessOption {
    const process: ProcessOption = [[0, dotsPos[0]]];
    for (let i = 0; i < dotsPos.length - 1; i++) {
      process.push([dotsPos[i], dotsPos[i + 1]]);
    }
    process.push([dotsPos[dotsPos.length - 1], 100]);
    return process;
  }
</script>
<style lang="scss">
  @import "vue-slider-component/lib/theme/default.scss";

  .merge-tooltip {
    position: absolute;
    left: 50%;
    bottom: 100%;
    transform: translate(-50%, -15px);
    font-size: 12px !important;
  }

  .loop-column-slider {
    $themeColor: var(--c-accent-1);
    $disabledOpacity: 0.5 !default;

    $railBorderRadius: 4px !default;

    $dotShadow: none;
    $dotShadowFocus: none;
    $dotBgColor: #fff !default;
    $dotBgColorDisable: #ccc !default;
    $dotBorderRadius: 50% !default;

    $stepBorderRadius: 50% !default;
    $stepBgColor: rgba(0, 0, 0, 0.16) !default;

    $labelFontSize: 14px !default;

    /* component style */
    .vue-slider-disabled {
      opacity: $disabledOpacity;
      cursor: not-allowed;
    }

    /* rail style */
    .vue-slider-rail {
      background-color: var(--c-g400);
      border-radius: $railBorderRadius;
    }

    /* process style */
    .vue-slider-process {
      background-color: $themeColor;
      border-radius: $railBorderRadius;
    }

    /* mark style */
    .vue-slider-mark {
      z-index: 4;

      &:first-child,
      &:last-child {
        .vue-slider-mark-step {
          display: none;
        }
      }

      @at-root &-step {
        width: 100%;
        height: 100%;
        border-radius: $stepBorderRadius;
        background-color: $stepBgColor;
      }

      @at-root &-label {
        font-size: $labelFontSize;
        white-space: nowrap;
      }
    }

    /* dot style */
    .vue-slider-dot {
      @at-root &-handle {
        cursor: pointer;
        width: 16px;
        height: 16px;
        border-radius: $dotBorderRadius;
        background-color: $dotBgColor;
        box-sizing: border-box;
        border: 3px solid var(--c-accent-1);
        box-shadow: $dotShadow;

        @at-root &-focus {
          box-shadow: $dotShadowFocus;
        }
        @at-root &-disabled {
          cursor: not-allowed;
          background-color: $dotBgColorDisable;
        }
      }

      @at-root &-tooltip {
        @at-root &-inner:not(.merge-tooltip) {
          display: none;
        }
      }

      @at-root &-tooltip-wrapper {
        display: none;
      }
    }
  }
</style>
